import type { Plant, PlantSpecies, PlantCategory, User, CreatePlantDto, UpdatePlantDto, CareActionDto, PlantMessage, CareAction, CareSchedule, JournalEntry } from '@/types';

// Re-export types for convenience
export type { CreatePlantDto, UpdatePlantDto, CareActionDto } from '@/types';

const API_BASE = '/api';

/** Normalize photo URL: ensure /uploads/ prefix for relative paths */
export function photoSrc(url: string | null | undefined): string {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('/uploads/') || url.startsWith('blob:')) return url;
  if (url.startsWith('uploads/')) return `/${url}`;
  return `/uploads/${url}`;
}

function getInitData(): string {
  // Get Telegram WebApp init data
  if (window.Telegram?.WebApp?.initData) {
    return window.Telegram.WebApp.initData;
  }
  return '';
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const initData = getInitData();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (initData) {
    headers['X-Telegram-Init-Data'] = initData;
  } else if (import.meta.env.DEV) {
    // Development mode - use dev user
    headers['X-Dev-User-Id'] = '12345678';
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// User API
export async function getCurrentUser(): Promise<User> {
  const { user } = await fetchApi<{ user: User }>('/users/me');
  return user;
}

export async function updateUserSettings(settings: {
  timezone?: string;
  notificationsEnabled?: boolean;
  languageCode?: string;
}): Promise<User> {
  const { user } = await fetchApi<{ user: User }>('/users/me/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
  return user;
}

// Unread messages API
export async function getUnreadCounts(): Promise<{ totalUnread: number; byPlant: Record<string, number> }> {
  return fetchApi<{ totalUnread: number; byPlant: Record<string, number> }>('/users/me/unread');
}

// Plants API
export async function getPlants(includeArchived = false): Promise<Plant[]> {
  const { plants } = await fetchApi<{ plants: Plant[] }>(
    `/plants${includeArchived ? '?includeArchived=true' : ''}`
  );
  return plants;
}

export async function getArchivedPlants(): Promise<Plant[]> {
  const { plants } = await fetchApi<{ plants: Plant[] }>('/plants?onlyArchived=true');
  return plants;
}

export async function getPlant(id: string): Promise<Plant> {
  const { plant } = await fetchApi<{ plant: Plant }>(`/plants/${id}`);
  return plant;
}

export async function createPlant(data: CreatePlantDto): Promise<Plant> {
  const { plant } = await fetchApi<{ plant: Plant }>('/plants', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return plant;
}

export async function updatePlant(id: string, data: UpdatePlantDto): Promise<Plant> {
  const { plant } = await fetchApi<{ plant: Plant }>(`/plants/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return plant;
}

export async function deletePlant(id: string): Promise<void> {
  await fetchApi(`/plants/${id}`, {
    method: 'DELETE',
  });
}

export async function archivePlant(id: string): Promise<Plant> {
  const { plant } = await fetchApi<{ plant: Plant }>(`/plants/${id}/archive`, {
    method: 'POST',
  });
  return plant;
}

export async function restorePlant(id: string): Promise<Plant> {
  const { plant } = await fetchApi<{ plant: Plant }>(`/plants/${id}/restore`, {
    method: 'POST',
  });
  return plant;
}

export async function waterBatch(plantIds: string[]): Promise<{ watered: number }> {
  return fetchApi<{ watered: number }>('/plants/water-batch', {
    method: 'POST',
    body: JSON.stringify({ plantIds }),
  });
}

// Care actions API
export async function recordCareAction(plantId: string, data: CareActionDto): Promise<CareAction> {
  const { action } = await fetchApi<{ action: CareAction }>(`/plants/${plantId}/care`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return action;
}

export async function getCareHistory(plantId: string, limit = 50, cursor?: string): Promise<CareAction[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  const { history } = await fetchApi<{ history: CareAction[] }>(
    `/plants/${plantId}/care?${params}`
  );
  return history;
}

// Messages API
export async function getPlantMessages(plantId: string, limit = 50, cursor?: string): Promise<PlantMessage[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  const { messages } = await fetchApi<{ messages: PlantMessage[] }>(
    `/plants/${plantId}/messages?${params}`
  );
  return messages;
}

export async function markMessagesAsRead(plantId: string): Promise<void> {
  await fetchApi(`/plants/${plantId}/messages/read`, {
    method: 'POST',
  });
}

// Photo upload API
export async function uploadPlantPhoto(plantId: string, file: File): Promise<Plant> {
  const formData = new FormData();
  formData.append('photo', file);

  const initData = getInitData();
  const headers: Record<string, string> = {};
  if (initData) {
    headers['X-Telegram-Init-Data'] = initData;
  } else if (import.meta.env.DEV) {
    headers['X-Dev-User-Id'] = '12345678';
  }

  const response = await fetch(`${API_BASE}/plants/${plantId}/photo`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  const { plant } = await response.json();
  return plant;
}

export async function uploadJournalPhoto(plantId: string, file: File): Promise<string> {
  const formData = new FormData();
  formData.append('photo', file);

  const initData = getInitData();
  const headers: Record<string, string> = {};
  if (initData) {
    headers['X-Telegram-Init-Data'] = initData;
  } else if (import.meta.env.DEV) {
    headers['X-Dev-User-Id'] = '12345678';
  }

  const response = await fetch(`${API_BASE}/plants/${plantId}/journal/photo`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  const { photoUrl } = await response.json();
  return photoUrl;
}

export async function deletePlantPhoto(plantId: string): Promise<Plant> {
  const { plant } = await fetchApi<{ plant: Plant }>(`/plants/${plantId}/photo`, {
    method: 'DELETE',
  });
  return plant;
}

// Care Schedules API
export async function getPlantSchedules(plantId: string): Promise<CareSchedule[]> {
  const { schedules } = await fetchApi<{ schedules: CareSchedule[] }>(`/plants/${plantId}/schedules`);
  return schedules;
}

export async function upsertCareSchedule(plantId: string, data: {
  actionType: string;
  frequencyDays: number;
  isEnabled?: boolean;
}): Promise<CareSchedule> {
  const { schedule } = await fetchApi<{ schedule: CareSchedule }>(`/plants/${plantId}/schedules`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return schedule;
}

// Journal API
export async function getJournalEntries(plantId: string, limit = 50): Promise<JournalEntry[]> {
  const { entries } = await fetchApi<{ entries: JournalEntry[] }>(
    `/plants/${plantId}/journal?limit=${limit}`
  );
  return entries;
}

export async function createJournalEntry(plantId: string, data: {
  content: string;
  photoUrl?: string;
  heightCm?: number;
}): Promise<JournalEntry> {
  const { entry } = await fetchApi<{ entry: JournalEntry }>(`/plants/${plantId}/journal`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return entry;
}

export async function updateJournalEntry(plantId: string, entryId: string, data: {
  content: string;
  heightCm?: number | null;
}): Promise<JournalEntry> {
  const { entry } = await fetchApi<{ entry: JournalEntry }>(`/plants/${plantId}/journal/${entryId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return entry;
}

export async function deleteJournalEntry(plantId: string, entryId: string): Promise<void> {
  await fetchApi(`/plants/${plantId}/journal/${entryId}`, {
    method: 'DELETE',
  });
}

// Species API
export async function getSpecies(category?: PlantCategory): Promise<PlantSpecies[]> {
  const params = category ? `?category=${category}` : '';
  const { species } = await fetchApi<{ species: PlantSpecies[] }>(`/species${params}`);
  return species;
}

export async function getSpeciesById(id: string): Promise<PlantSpecies> {
  const { species } = await fetchApi<{ species: PlantSpecies }>(`/species/${id}`);
  return species;
}

// Stats API
export interface Achievement {
  id: string;
  name: string;
  description: string;
  emoji: string;
  unlocked: boolean;
}

export interface UserStats {
  wateringsThisMonth: number;
  totalCareActions: number;
  totalPlants: number;
  currentStreak: number;
  bestStreak: number;
  mostCaredPlant: { nickname: string; actions: number } | null;
  careBreakdown: Record<string, number>;
  heatmap: Record<string, number>;
  achievements: Achievement[];
}

export async function getStats(days?: number): Promise<UserStats> {
  const params = days ? `?days=${days}` : '';
  const { stats } = await fetchApi<{ stats: UserStats }>(`/stats${params}`);
  return stats;
}

export async function getUserCareHistory(days?: number): Promise<CareAction[]> {
  const params = days ? `?days=${days}` : '';
  const { history } = await fetchApi<{ history: CareAction[] }>(`/stats/care-history${params}`);
  return history;
}
